import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FormGrid, ListGrid, PrimaryButton, GhostButton, ConfirmDialog } from "./ui";

// demo-ui.html deseni: üstte "Ekle / Güncelle" formu, altta kayıt listesi.
// Satıra tıklayınca kayıt forma yüklenir (id doluysa PUT, boşsa POST).
//
// config: { title, listTitle, sub, idKey, columns, fields, api:{list,create,update,remove} }
//   fields: [{ name, label, type:"text"|"number"|"kurus", options?, required?, parse?, format? }]
export default function FormListPanel({ config }) {
  const {
    idKey,
    columns,
    fields,
    api,
    title,
    listTitle,
    sub,
    useDialog = false,
    addLabel = `${title} Ekle`,
  } = config;

  const emptyForm = useCallback(
    () => fields.reduce((a, f) => ({ ...a, [f.name]: f.default ?? "" }), { [idKey]: "" }),
    [fields, idKey]
  );

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // onay bekleyen kayıt id'si
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list());
    } catch (e) {
      showSnackbar(e.message || "Liste alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [api, showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setForm(emptyForm());

  const fillForm = (row) => {
    setForm(
      fields.reduce(
        (a, f) => {
          const raw = row[f.name];
          a[f.name] = f.format && raw != null ? f.format(raw) : raw ?? "";
          return a;
        },
        { [idKey]: row[idKey] }
      )
    );
  };

  const handleRowClick = ({ row }) => fillForm(row);

  const openCreateDialog = () => {
    reset();
    setDialogOpen(true);
  };

  const openUpdateDialog = (row) => {
    fillForm(row);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    reset();
  };

  const handleSave = async () => {
    const missing = fields.find((f) => f.required && form[f.name] === "");
    if (missing) {
      showSnackbar(`${missing.label} zorunlu`, "error");
      return;
    }

    // Form değerleri string tutulur; API'ye tipiyle gider.
    const dto = fields.reduce((a, f) => {
      const raw = form[f.name];
      if (f.type === "number" || f.type === "select") a[f.name] = raw === "" ? null : Number(raw);
      else if (f.parse) a[f.name] = f.parse(raw);
      else a[f.name] = raw;
      return a;
    }, {});

    const isUpdate = form[idKey] !== "" && form[idKey] != null;
    setSaving(true);
    try {
      if (isUpdate) {
        await api.update({ ...dto, [idKey]: Number(form[idKey]) });
        showSnackbar("Kayıt güncellendi", "success");
      } else {
        await api.create(dto);
        showSnackbar("Kayıt eklendi", "success");
      }
      if (useDialog) setDialogOpen(false);
      reset();
      load();
    } catch (e) {
      showSnackbar(e.message || "Kaydedilemedi", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setPendingDelete(null);
    try {
      await api.remove(id);
      showSnackbar("Kayıt silindi", "success");
      if (Number(form[idKey]) === Number(id)) {
        if (useDialog) setDialogOpen(false);
        reset();
      }
      load();
    } catch (e) {
      showSnackbar(e.message || "Silinemedi", "error");
    }
  };

  const gridColumns = [
    ...columns,
    {
      field: "__actions",
      type: "actions",
      headerName: "",
      width: useDialog ? 100 : 60,
      getActions: (params) => [
        ...(useDialog
          ? [
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon fontSize="small" />}
                label="Güncelle"
                onClick={(e) => {
                  e.stopPropagation();
                  openUpdateDialog(params.row);
                }}
              />,
            ]
          : []),
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon fontSize="small" />}
          label="Sil"
          onClick={(e) => {
            e.stopPropagation(); // satır tıklaması forma yüklemesin
            setPendingDelete(params.row[idKey]);
          }}
        />,
      ],
    },
  ];

  const isUpdate = form[idKey] !== "" && form[idKey] != null;

  const formFields = (
    <FormGrid>
      {fields.filter((f) => !f.hidden).map((f) => (
        <TextField
          key={f.name}
          select={f.type === "select"}
          type={f.type === "number" ? "number" : "text"}
          label={f.label}
          value={form[f.name] ?? ""}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
          required={f.required}
          disabled={f.disabled}
          helperText={f.helperText}
          size="small"
          fullWidth
        >
          {f.type === "select" &&
            (f.options || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
        </TextField>
      ))}
    </FormGrid>
  );

  return (
    <>
      {!useDialog && (
        <Panel
          title={`${title} Ekle / Güncelle`}
          sub={sub || "Boş kayıt eklenir, listeden seçilen kayıt güncellenir."}
        >
          {formFields}

          <Box sx={{ display: "flex", gap: 1.5, mt: 2.5 }}>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {isUpdate ? "Güncelle" : "Kaydet"}
            </PrimaryButton>
            <GhostButton
              startIcon={<CleaningServicesRoundedIcon />}
              onClick={reset}
            >
              Temizle
            </GhostButton>
          </Box>
        </Panel>
      )}

      <Panel
        title={listTitle || `Kayıtlı ${title}`}
        sub={
          useDialog
            ? "Güncellemek için satırdaki düzenleme simgesini kullanın."
            : "Düzenlemek için satıra tıklayın."
        }
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {useDialog && (
              <PrimaryButton startIcon={<AddIcon />} onClick={openCreateDialog}>
                {addLabel}
              </PrimaryButton>
            )}
            <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Yenile
            </GhostButton>
          </Box>
        }
      >
        <ListGrid
          rows={rows}
          columns={gridColumns}
          getRowId={(r) => r[idKey]}
          loading={loading}
          onRowClick={useDialog ? undefined : handleRowClick}
        />
      </Panel>

      {useDialog && (
        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>{isUpdate ? `${title} Güncelle` : addLabel}</DialogTitle>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {formFields}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <GhostButton onClick={closeDialog} disabled={saving}>
              İptal
            </GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {isUpdate ? "Güncelle" : "Kaydet"}
            </PrimaryButton>
          </DialogActions>
        </Dialog>
      )}

      <ConfirmDialog
        open={pendingDelete != null}
        message={`Bu ${title.toLowerCase()} kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => handleDelete(pendingDelete)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
